/**
 * TOTP / HOTP using the Web Crypto API (RFC 4226 + RFC 6238).
 * Zero runtime dependencies.
 */

import { decodeBase32 } from "./base32.ts";

export type HashAlgorithm = "SHA-1" | "SHA-256" | "SHA-512";

export type TotpOptions = {
  /** Base32-encoded shared secret. */
  secret: string;
  /** Code length (typically 6–8). Default 6. */
  digits?: number;
  /** Time step in seconds. Default 30. */
  period?: number;
  /** HMAC hash algorithm. Default SHA-1. */
  algorithm?: HashAlgorithm;
  /** Unix time in seconds. Defaults to now. */
  timestamp?: number;
};

function assertPeriod(period: number): void {
  if (!Number.isFinite(period) || period <= 0) {
    throw new RangeError("period must be a positive number");
  }
}

function toCounterBytes(counter: bigint): Uint8Array<ArrayBuffer> {
  const buf = new Uint8Array(8);
  let value = counter;
  for (let i = 7; i >= 0; i--) {
    buf[i] = Number(value & 0xffn);
    value >>= 8n;
  }
  return buf;
}

function dynamicTruncate(hmac: ArrayBuffer, digits: number): string {
  const bytes = new Uint8Array(hmac);
  const last = bytes[bytes.length - 1];
  if (last === undefined) {
    throw new Error("Empty HMAC");
  }
  const offset = last & 0x0f;
  const b0 = bytes[offset];
  const b1 = bytes[offset + 1];
  const b2 = bytes[offset + 2];
  const b3 = bytes[offset + 3];
  if (b0 === undefined || b1 === undefined || b2 === undefined || b3 === undefined) {
    throw new Error("HMAC too short for dynamic truncation");
  }
  const binary =
    ((b0 & 0x7f) << 24) | ((b1 & 0xff) << 16) | ((b2 & 0xff) << 8) | (b3 & 0xff);
  const otp = binary % 10 ** digits;
  return otp.toString().padStart(digits, "0");
}

async function hmac(
  keyBytes: Uint8Array<ArrayBuffer>,
  message: Uint8Array<ArrayBuffer>,
  algorithm: HashAlgorithm,
): Promise<ArrayBuffer> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "HMAC", hash: algorithm },
    false,
    ["sign"],
  );
  return crypto.subtle.sign("HMAC", cryptoKey, message);
}

/** HOTP (RFC 4226). */
export async function hotp(
  secretBytes: Uint8Array<ArrayBuffer>,
  counter: bigint,
  digits: number,
  algorithm: HashAlgorithm,
): Promise<string> {
  if (!Number.isInteger(digits) || digits < 6 || digits > 10) {
    throw new RangeError("digits must be an integer between 6 and 10");
  }
  const mac = await hmac(secretBytes, toCounterBytes(counter), algorithm);
  return dynamicTruncate(mac, digits);
}

/** Seconds remaining in the current TOTP window. */
export function secondsRemaining(period: number, timestamp = Date.now() / 1000): number {
  assertPeriod(period);
  const elapsed = Math.floor(timestamp) % period;
  return period - elapsed;
}

/** Floor counter for the given period. */
export function timeCounter(period: number, timestamp = Date.now() / 1000): bigint {
  assertPeriod(period);
  return BigInt(Math.floor(timestamp / period));
}

/** Generate a TOTP code. */
export async function generateTotp(options: TotpOptions): Promise<string> {
  const digits = options.digits ?? 6;
  const period = options.period ?? 30;
  const algorithm = options.algorithm ?? "SHA-1";
  const timestamp = options.timestamp ?? Date.now() / 1000;

  const secretBytes = decodeBase32(options.secret);
  if (secretBytes.length === 0) {
    throw new Error("Secret is empty");
  }

  const counter = timeCounter(period, timestamp);
  return hotp(secretBytes, counter, digits, algorithm);
}

/** Map friendly algorithm names (URL/UI) to Web Crypto names. */
export function parseAlgorithm(value: string): HashAlgorithm {
  const normalized = value.trim().toUpperCase().replaceAll("_", "-");
  switch (normalized) {
    case "SHA1":
    case "SHA-1":
      return "SHA-1";
    case "SHA256":
    case "SHA-256":
      return "SHA-256";
    case "SHA512":
    case "SHA-512":
      return "SHA-512";
    default:
      throw new Error(`Unsupported algorithm: ${value}`);
  }
}

/** Short label for UI / otpauth (SHA1, SHA256, SHA512). */
export function algorithmLabel(algorithm: HashAlgorithm): string {
  switch (algorithm) {
    case "SHA-1":
      return "SHA1";
    case "SHA-256":
      return "SHA256";
    case "SHA-512":
      return "SHA512";
  }
}
